/**
 * Navegación del módulo de administración.
 *
 * La barra lateral (`layout/nav.ts`) declara los módulos de forma estática y
 * filtra por rol. Acá se resuelve lo que la lateral no puede: una sección que
 * **existe o no existe según la marca**. El portal del consumidor es ese caso
 * — ADR-011 lo prohíbe en tenants farmacéuticos — y la regla del issue es
 * clara: en una marca pharma no alcanza con deshabilitarlo, no tiene que
 * estar en la navegación.
 *
 * Por eso la solapa se agrega **sólo** si `GET /consumer/portal/config`
 * contestó `portal_enabled: true`. Mientras la consulta viaja, tampoco está:
 * el estado inicial es "no", no "quizás".
 */
import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';

import { useI18n } from '@/i18n/useI18n';
import { cn } from '@/ui/cn';
import { isPortalEnabled, usePortalConfig } from '../portal/usePortalConfig';

interface AdminTab {
  to: string;
  label: string;
}

export function AdminNav(): ReactNode {
  const { t } = useI18n();
  const portal = usePortalConfig();

  const tabs: AdminTab[] = [
    { to: '/admin/users', label: t('admin.tab.users') },
    { to: '/admin/audit', label: t('admin.tab.audit') },
  ];
  if (isPortalEnabled(portal)) {
    tabs.push({ to: '/admin/portal', label: t('admin.tab.portal') });
  }

  return (
    <nav aria-label={t('admin.sections')} className="mb-4">
      <ul className="flex flex-wrap gap-1 border-b border-border">
        {tabs.map((tab) => (
          <li key={tab.to}>
            <NavLink
              to={tab.to}
              className={({ isActive }) =>
                cn(
                  '-mb-px inline-block border-b-2 px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'border-accent text-accent'
                    : 'border-transparent text-ink-muted hover:text-ink',
                )
              }
            >
              {tab.label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
